import React, { useState, useEffect, useContext } from "react";
import MainContainer from "../../components/MainContainer";
import { makeStyles, Paper, Tabs, Tab } from "@material-ui/core";

import TabPanel from "../../components/TabPanel";

import SchedulesForm from "../../components/SchedulesForm";
import CompaniesManager from "../../components/CompaniesManager";
import PlansManager from "../../components/PlansManager";
import HelpsManager from "../../components/HelpsManager";
import Options from "../../components/Settings/Options";
import Whitelabel from "../../components/Settings/Whitelabel";
import UploaderCert from "../../components/Settings/UploaderCert";
import VersionManager from "../../components/VersionManager";
import { i18n } from "../../translate/i18n.js";
import { toast } from "react-toastify";

import useCompanies from "../../hooks/useCompanies";
import { AuthContext } from "../../context/Auth/AuthContext";

import OnlyForSuperUser from "../../components/OnlyForSuperUser";
import useCompanySettings from "../../hooks/useSettings/companySettings";
import useSettings from "../../hooks/useSettings";
import ForbiddenPage from "../../components/ForbiddenPage/index.js";
import Empresa from "../../pages/Empresa";

import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import Box from '@material-ui/core/Box';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { useTheme } from '@material-ui/core/styles';

const useStyles = makeStyles((theme) => ({
  root: {
    flex: 1,
    backgroundColor: theme.palette.background.default,
  },
  mainPaper: {
    ...theme.scrollbarStyles,
    overflowY: "scroll",
    flex: 1,
    backgroundColor: "transparent",
    border: "none",
  },
  headerContainer: {
    background: "linear-gradient(90deg, #6517ab 0%, #8e44ad 100%)",
    padding: theme.spacing(3),
    borderRadius: "0 0 20px 20px",
    color: "white",
    marginBottom: theme.spacing(3),
    boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
  },
  headerTitle: {
    fontSize: "2rem",
    fontWeight: 800,
    marginBottom: theme.spacing(1),
  },
  headerSubtitle: {
    fontSize: "1rem",
    opacity: 0.9,
    marginBottom: theme.spacing(2),
  },
  tabsContainer: {
    marginTop: theme.spacing(2),
  },
  tabs: {
    minHeight: "40px",
    "& .MuiTabs-indicator": {
      backgroundColor: "white",
      height: "3px",
      borderRadius: "3px",
    },
  },
  tab: {
    textTransform: "none",
    fontWeight: 700,
    fontSize: "1rem",
    color: "rgba(255, 255, 255, 0.7)",
    minWidth: "120px",
    minHeight: "40px",
    "&.Mui-selected": {
      color: "white",
    },
  },
  contentPaper: {
    backgroundColor: theme.palette.background.paper,
    borderRadius: "20px",
    padding: theme.spacing(3),
    boxShadow: "0 4px 15px rgba(0,0,0,0.05)",
    margin: theme.spacing(0, 2, 2, 2),
  },
  container: {
    width: "100%",
  },
}));

const SettingsCustom = () => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [tab, setTab] = useState("options");
  const [schedules, setSchedules] = useState([]);
  const [company, setCompany] = useState({});
  const [loading, setLoading] = useState(false);
  const [currentUser, setCurrentUser] = useState({});
  const [settings, setSettings] = useState({});
  const [oldSettings, setOldSettings] = useState({});
  const [schedulesEnabled, setSchedulesEnabled] = useState(false);

  const { find, updateSchedules } = useCompanies();
  const { getAll: getAllSettings } = useCompanySettings();
  const { getAll: getAllSettingsOld } = useSettings();
  const { user } = useContext(AuthContext);

  useEffect(() => {
    async function findData() {
      setLoading(true);
      try {
        const companyId = user.companyId;
        const company = await find(companyId);
        const settingList = await getAllSettings(companyId);
        const settingListOld = await getAllSettingsOld();

        setCompany(company);
        setSchedules(company.schedules);
        setSettings(settingList);
        setOldSettings(settingListOld);
        setSchedulesEnabled(settingList.scheduleType === "company");
        setCurrentUser(user);
      } catch (e) {
        toast.error(e);
      }
      setLoading(false);
    }
    findData();
  }, []);

  const handleTabChange = (event, newValue) => {
    setTab(newValue);
  };

  const handleSubmitSchedules = async (data) => {
    setLoading(true);
    try {
      setSchedules(data);
      await updateSchedules({ id: company.id, schedules: data });
      toast.success("Horários atualizados com sucesso.");
    } catch (e) {
      toast.error(e);
    }
    setLoading(false);
  };

  const isSuper = () => currentUser.super;

  return (
    <MainContainer className={classes.root}>
      {user.profile === "user" ?
        <ForbiddenPage />
        :
        <Box className={classes.mainPaper}>
          <Box className={classes.headerContainer}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12}>
                <Typography variant="h5" className={classes.headerTitle}>{i18n.t("settings.title")}</Typography>
                <Typography className={classes.headerSubtitle}>Gerencie as configurações e preferências do seu sistema</Typography>
              </Grid>
            </Grid>

            <div className={classes.tabsContainer}>
              <Tabs
                value={tab}
                onChange={handleTabChange}
                variant="scrollable"
                scrollButtons="auto"
                className={classes.tabs}
              >
                <Tab label={i18n.t("settings.tabs.options")} value={"options"} className={classes.tab} />
                {schedulesEnabled && <Tab label="Horários" value={"schedules"} className={classes.tab} />}
                {isSuper() && <Tab label="Empresas" value={"companies"} className={classes.tab} />}
                {isSuper() && <Tab label={i18n.t("settings.tabs.plans")} value={"plans"} className={classes.tab} />}
                {isSuper() && <Tab label={i18n.t("settings.tabs.helps")} value={"helps"} className={classes.tab} />}
                {isSuper() && <Tab label="Whitelabel" value={"whitelabel"} className={classes.tab} />}
                {isSuper() && <Tab label="Certificado Efí PIX" value={"uploadercert"} className={classes.tab} />}
                {isSuper() && <Tab label="Cadastro" value={"cadastro"} className={classes.tab} />}
                {isSuper() && <Tab label="Versão" value={"version"} className={classes.tab} />}
              </Tabs>
            </div>
          </Box>

          <Paper className={classes.contentPaper} elevation={0}>
            <TabPanel className={classes.container} value={tab} name={"schedules"}>
              <SchedulesForm
                loading={loading}
                onSubmit={handleSubmitSchedules}
                initialValues={schedules}
              />
            </TabPanel>

            <OnlyForSuperUser user={currentUser} yes={() => (
              <>
                <TabPanel className={classes.container} value={tab} name={"companies"}><CompaniesManager /></TabPanel>
                <TabPanel className={classes.container} value={tab} name={"plans"}><PlansManager /></TabPanel>
                <TabPanel className={classes.container} value={tab} name={"helps"}><HelpsManager /></TabPanel>
                <TabPanel className={classes.container} value={tab} name={"whitelabel"}><Whitelabel settings={oldSettings} /></TabPanel>
                <TabPanel className={classes.container} value={tab} name={"uploadercert"}><UploaderCert /></TabPanel>
                <TabPanel className={classes.container} value={tab} name={"cadastro"}><Empresa /></TabPanel>
                <TabPanel className={classes.container} value={tab} name={"version"}><VersionManager /></TabPanel>
              </>
            )} />

            <TabPanel className={classes.container} value={tab} name={"options"}>
              <Options
                settings={settings}
                oldSettings={oldSettings}
                user={currentUser}
                scheduleTypeChanged={(value) => setSchedulesEnabled(value === "company")}
              />
            </TabPanel>
          </Paper>
        </Box>
      }
    </MainContainer>
  );
};

export default SettingsCustom;